/**
 * Interface between main thread and core web worker.
 *
 * Also open communication channel between core web worker and render web worker.
 */

import { setFileLoadingProgress } from '@/app/atoms/fileLoadingAtom';
import { debugFlag } from '@/app/debugFlags/debugFlags';
import { events } from '@/app/events/events';
import { sheets } from '@/app/grid/controller/Sheets';
import type { ShiftAxis } from '@/app/grid/documentSources/cellDocumentSources';
import { cellDocumentSources } from '@/app/grid/documentSources/cellDocumentSources';
import type { ColumnRowResize } from '@/app/gridGL/interaction/pointer/PointerHeading';
import { content } from '@/app/gridGL/pixiApp/contentInstance';
import * as gridSettings from '@/app/gridGL/pixiApp/gridSettings';
import { getIsEmbedMode } from '@/app/helpers/sharedArrayBufferSupport';
import { isNative } from '@/app/nativeBridge';
import type {
  BorderSelection,
  BorderStyle,
  CellAlign,
  CellFormatSummary,
  CellVerticalAlign,
  CellWrap,
  ChartUpdate,
  CodeCellLanguage,
  CommentThread,
  ConditionalFormatClient,
  ConditionalFormatUpdate,
  DataTableSort,
  FormatUpdate,
  JsBordersSheet,
  JsCellValue,
  JsChart,
  JsChartLayout,
  JsClipboard,
  JsCodeCell,
  JsCodeErrorContext,
  JsCodeSummaryRequest,
  JsColumnFilterCondition,
  JsColumnFilterInfo,
  JsCoordinate,
  JsDataTableColumnHeader,
  JsGetAICellResult,
  JsHashValidationWarnings,
  JsHtmlOutput,
  JsOffset,
  JsPivotFieldItem,
  JsPivotSourceField,
  JsReplaceResult,
  JsResponse,
  JsSearchResult,
  JsSheetFill,
  JsSheetNameToColor,
  JsSheetPagination,
  JsSheetPos,
  JsSummarizeSelectionResult,
  JsSummaryContext,
  JsUpdateCodeCell,
  PasteSpecial,
  PivotSource,
  PivotSpec,
  Pos,
  SearchOptions,
  SheetBounds,
  SheetInfo,
  SheetRect,
  TextSpan,
  TrackedTransaction,
  UndoRedoResponse,
  Validation,
  ValidationUpdate,
} from '@/app/quadratic-core-types';
import { JsMergeCells, SheetContentCache, SheetDataTablesCache } from '@/app/quadratic-core/quadratic_core';
import { fromUint8Array } from '@/app/shared/utils/Uint8Array';
import type { SheetPrintSetupPayload } from '@/app/ui/menus/TopBar/PageSetup/pageSetupState';
import type { CodeRun } from '@/app/web-workers/CodeRun';
import { javascriptWebWorker } from '@/app/web-workers/javascriptWebWorker/javascriptWebWorker';
import { pythonWebWorker } from '@/app/web-workers/pythonWebWorker/pythonWebWorker';
import type {
  ClientCoreGetCellFormatSummary,
  ClientCoreGetCodeCell,
  ClientCoreGetDisplayCell,
  ClientCoreGetEditCell,
  ClientCoreImportFile,
  ClientCoreImportWouldCoverContent,
  ClientCoreLoad,
  ClientCoreMessage,
  ClientCoreProxyFetchResponse,
  ClientCoreSummarizeSelection,
  ClientCoreUpgradeGridFile,
  CodeOperation,
  CoreClientAddSheetResponse,
  CoreClientBatchUpdateConditionalFormats,
  CoreClientClearDataTableFilters,
  CoreClientCodeExecutionState,
  CoreClientCopyChartToClipboard,
  CoreClientCopyToClipboard,
  CoreClientCreatePivotTable,
  CoreClientCreatePivotTableOnNewSheet,
  CoreClientCutToClipboard,
  CoreClientDataTableFirstRowAsHeader,
  CoreClientDataTableMeta,
  CoreClientDeleteCellValues,
  CoreClientDeleteColumns,
  CoreClientDeleteDefinedNameResponse,
  CoreClientDeleteRows,
  CoreClientDeleteSheetResponse,
  CoreClientDeleteValuesAndFormatting,
  CoreClientDuplicateChart,
  CoreClientDuplicateSheetResponse,
  CoreClientExport,
  CoreClientExportCsvSelection,
  CoreClientExportExcel,
  CoreClientExportJson,
  CoreClientExportParquetSelection,
  CoreClientExportPdf,
  CoreClientFilterDataTableColumnCondition,
  CoreClientFilterSheetColumnCondition,
  CoreClientGetAICells,
  CoreClientGetAICodeErrors,
  CoreClientGetAIFormats,
  CoreClientGetAISelectionContexts,
  CoreClientGetAITransactions,
  CoreClientGetCellFormatSummary,
  CoreClientGetCellValue,
  CoreClientGetChartPreview,
  CoreClientGetChartUpdate,
  CoreClientGetChartUpdateFromSelection,
  CoreClientGetChartUpdatePreview,
  CoreClientGetCodeCell,
  CoreClientGetDataTableColumnFilterInfo,
  CoreClientGetDisplayCell,
  CoreClientGetEditCell,
  CoreClientGetFormatSelection,
  CoreClientGetJwt,
  CoreClientGetPivotFieldItems,
  CoreClientGetPivotSourceFields,
  CoreClientGetPivotSpec,
  CoreClientGetPivotTableByName,
  CoreClientGetSheetColumnFilterInfo,
  CoreClientGetSheetPagination,
  CoreClientGetValidationFromPos,
  CoreClientGetValidationList,
  CoreClientGetValidations,
  CoreClientGridToDataTable,
  CoreClientHasCellData,
  CoreClientHideColumns,
  CoreClientHideRows,
  CoreClientImportFile,
  CoreClientImportWouldCoverContent,
  CoreClientInsertColumns,
  CoreClientInsertRows,
  CoreClientLoad,
  CoreClientMergeCells,
  CoreClientMergeCellsBatchResponse,
  CoreClientMergeCellsResponse,
  CoreClientMessage,
  CoreClientMoveCodeCellHorizontally,
  CoreClientMoveCodeCellVertically,
  CoreClientMoveSheetResponse,
  CoreClientNeighborText,
  CoreClientPivotDrillToDetail,
  CoreClientPreviewConditionalFormat,
  CoreClientProxyFetch,
  CoreClientRedoResponse,
  CoreClientRemoveValidationSelection,
  CoreClientRerunCodeCells,
  CoreClientResizeColumns,
  CoreClientSearch,
  CoreClientSearchReplace,
  CoreClientSetBorders,
  CoreClientSetCellImage,
  CoreClientSetCellRenderResize,
  CoreClientSetCodeCellValue,
  CoreClientSetDefinedNameResponse,
  CoreClientSetFormats,
  CoreClientSetFormatsA1,
  CoreClientSetFormula,
  CoreClientSetFormulas,
  CoreClientSetPivotTable,
  CoreClientSetSheetColorResponse,
  CoreClientSetSheetFrozenResponse,
  CoreClientSetSheetNameResponse,
  CoreClientSetSheetPrintSetupResponse,
  CoreClientSetSheetsColorResponse,
  CoreClientSummarizeSelection,
  CoreClientUndoResponse,
  CoreClientUnmergeCellsBatchResponse,
  CoreClientUnmergeCellsResponse,
  CoreClientUpdateChart,
  CoreClientUpdateCommentThread,
  CoreClientUpdateConditionalFormat,
  CoreClientUpdateValidation,
  CoreClientUpgradeFile,
  CoreClientValidateInput,
  JsEditCell,
  PivotTableLocation,
} from '@/app/web-workers/quadraticCore/coreClientMessages';
import { wasmPerfReporter } from '@/app/web-workers/quadraticCore/wasmPerfReporter';
import { renderWebWorker } from '@/app/web-workers/renderWebWorker/renderWebWorker';
import {
  compileWasmModule,
  isWasmAssetUnavailableError,
  isWasmUnsupportedBrowserError,
} from '@/app/web-workers/wasmModule';
import { authClient } from '@/auth/auth';
import { formatWorkerErrorMessage, isBrowserAbortError, isLikelyBrowserAbort } from '@/shared/utils/browserLifecycle';
import type { CellSources } from 'quadratic-shared/typesAndSchemasDocuments';

export type TransactionProvenance = {
  actor: 'User' | 'AI' | 'Unknown';
  user?: {
    id?: string;
    email?: string;
    name?: string;
  };
  ai?: {
    chatId: string;
    messageIndex: number;
    toolCallId?: string;
    source?: string;
  };
  timestamp: number;
};

/**
 * Move document sources along with the cells a delete shifted, one line at a
 * time and highest-first, so each shift only has to close its own gap. Mirrors
 * how core projects the same operation for the server.
 */
const shiftCellDocumentSources = (sheetId: string, axis: ShiftAxis, lines: number[]) => {
  for (const at of [...new Set(lines)].sort((a, b) => b - a)) {
    cellDocumentSources.shift({ sheetId, axis, at, delta: -1, deleteAt: true });
  }
};

class QuadraticCore {
  private worker?: Worker;
  private id = 0;
  private waitingForResponse: Record<number, Function> = {};
  private _teamUuid?: string;
  private transactionProvenance?: TransactionProvenance;

  // This is a hack to get import files to properly show negative offsets dialog
  // after importing from dashboard. This can be removed in the future.
  receivedClientMessage = false;

  // Most recent code running state emitted from core, cached so callers can
  // synchronously check whether the code queue is currently idle without
  // waiting for the next `codeRunningState` event. Undefined until core has
  // emitted at least one state.
  private _codeRunningState?: { current?: CodeRun; awaitingExecution: CodeRun[] };

  getTeamUuid(): string | null {
    return this._teamUuid ?? null;
  }

  // Returns the last code running state emitted by core, or undefined if none
  // has been emitted yet. Used to detect an already-idle queue immediately
  // rather than waiting for an event that may have already fired.
  get codeRunningState(): { current?: CodeRun; awaitingExecution: CodeRun[] } | undefined {
    return this._codeRunningState;
  }

  initWorker() {
    if (!this.worker) {
      this.worker = new Worker(new URL('./worker/core.worker.ts', import.meta.url), { type: 'module' });
      this.worker.onmessage = this.handleMessage;
      this.worker.onerror = (e) => {
        const formatted = formatWorkerErrorMessage(e, 'core.worker error (no details available)');
        const suffix = isLikelyBrowserAbort(e) ? ' (suppressed, browser-induced abort)' : '';
        console.warn(`[core.worker] error${suffix}: ${formatted}`, e);
      };

      this.sendInit();

      // Share the single main-thread-compiled wasm module so the worker
      // instantiates the same compiled code instead of fetching/compiling its
      // own copy. This is intentionally fire-and-forget: clientCoreInitWasm may
      // race with loadFile, and either ordering is safe. The worker's initWasm
      // dedupes via a shared promise, and loadFile falls back to compiling its
      // own copy if clientCoreInitWasm hasn't arrived yet. Whichever path wins,
      // the module is only instantiated once. If compilation fails the worker
      // still recovers via the loadFile fallback, so we only log here.
      compileWasmModule()
        .then((module) => this.send({ type: 'clientCoreInitWasm', module }))
        .catch((e) => {
          // A stale wasm asset after a deploy is an expected client/deploy-race
          // condition that's already recovering via a reload — log at warn so it
          // doesn't pollute the Sentry error feed. If a reload wasn't triggered
          // (persistent failure after we already reloaded once), it's a genuine
          // problem, so keep it at error.
          if (isWasmAssetUnavailableError(e) && e.reloadTriggered) {
            console.warn('[quadraticCore] wasm asset unavailable, reloading for fresh assets:', e);
          } else if (isWasmUnsupportedBrowserError(e)) {
            // Same unsupported-engine condition as in renderWebWorker; both
            // consumers await the one shared `compileWasmModule()` promise, so
            // it rejects both. The user already sees "Browser not supported".
            console.warn('[quadraticCore] wasm not supported by this browser:', e);
          } else if (isBrowserAbortError(e)) {
            // Same navigation-cancelled preload as in renderWebWorker: both
            // consumers await the one shared `compileWasmModule()` promise, so
            // an abort rejects both. Not an error — the page is leaving.
            console.warn('[quadraticCore] wasm compile aborted by navigation:', e);
          } else {
            console.error('[quadraticCore] Failed to compile wasm module:', e);
          }
        });

      // Begin aggregating any quadratic-core wasm profiling samples (no-op
      // unless the wasm bundle was built with the core-profiler feature).
      wasmPerfReporter.init();
    }
  }

  private handleMessage = async (e: MessageEvent<CoreClientMessage>) => {
    if (debugFlag('debugWebWorkersMessages')) console.log(`[quadraticCore] message: ${e.data.type}`);

    if (e.data.type === 'coreClientProxyFetch') {
      await this.handleProxyFetch(e.data);
      return;
    }

    // quadratic-core initiated messages
    if (e.data.type === 'coreClientAddSheet') {
      events.emit('addSheet', fromUint8Array<SheetInfo>(e.data.sheetInfo), e.data.user);
      return;
    } else if (e.data.type === 'coreClientSheetsInfo') {
      events.emit('sheetsInfo', fromUint8Array<SheetInfo[]>(e.data.sheetsInfo));
      return;
    } else if (e.data.type === 'coreClientHashRenderFills') {
      events.emit('hashRenderFills', e.data.hashRenderFills);
      return;
    } else if (e.data.type === 'coreClientHashesDirtyFills') {
      events.emit('hashesDirtyFills', e.data.dirtyHashes);
      return;
    } else if (e.data.type === 'coreClientDeleteSheet') {
      events.emit('deleteSheet', e.data.sheetId, e.data.user);
      return;
    } else if (e.data.type === 'coreClientSheetInfoUpdate') {
      events.emit('sheetInfoUpdate', fromUint8Array<SheetInfo>(e.data.sheetInfo));
      return;
    } else if (e.data.type === 'coreClientSetCursor') {
      events.emit('setCursor', e.data.cursor);
      return;
    } else if (e.data.type === 'coreClientSheetOffsets') {
      events.emit('sheetOffsets', e.data.sheetId, fromUint8Array<JsOffset[]>(e.data.offsets));
      return;
    } else if (e.data.type === 'coreClientHtmlOutput') {
      events.emit('htmlOutput', fromUint8Array<JsHtmlOutput[]>(e.data.html));
      return;
    } else if (e.data.type === 'coreClientUpdateHtml') {
      events.emit('htmlUpdate', fromUint8Array<JsHtmlOutput>(e.data.html));
      return;
    } else if (e.data.type === 'coreClientGenerateThumbnail') {
      events.emit('generateThumbnail');
      return;
    } else if (e.data.type === 'coreClientSheetCodeCells') {
      events.emit('renderCodeCells', e.data.sheetId, e.data.renderCodeCells);
      return;
    } else if (e.data.type === 'coreClientSheetBoundsUpdate') {
      events.emit('sheetBounds', fromUint8Array<SheetBounds>(e.data.sheetBounds));
      return;
    } else if (e.data.type === 'coreClientImportProgress') {
      events.emit('importProgress', e.data);
      return;
    } else if (e.data.type === 'coreClientTransactionStart') {
      events.emit('transactionStart', e.data);
      return;
    } else if (e.data.type === 'coreClientTransactionEnd') {
      events.emit('transactionEnd', e.data);
      return;
    } else if (e.data.type === 'coreClientCellEditHistory') {
      events.emit('cellEditHistory', e.data);
      return;
    } else if (e.data.type === 'coreClientUpdateCodeCells') {
      events.emit('updateCodeCells', fromUint8Array<JsUpdateCodeCell[]>(e.data.updateCodeCells));
      return;
    } else if (e.data.type === 'coreClientRequestCodeSummaries') {
      events.emit('requestCodeSummaries', fromUint8Array<JsCodeSummaryRequest[]>(e.data.summaries));
      return;
    } else if (e.data.type === 'coreClientMultiplayerState') {
      events.emit('multiplayerState', e.data.state);
      return;
    } else if (e.data.type === 'coreClientCodeRunningState') {
      try {
        const data = e.data as CoreClientCodeExecutionState;
        const state = data.codeRunningState;

        // Parse current operation if present
        let current: CodeRun | undefined;
        if (state.current) {
          current = {
            transactionId: data.transactionId,
            sheetPos: { x: state.current.x, y: state.current.y, sheetId: state.current.sheet_id },
            code: '', // Code is not needed for display, can be retrieved from grid if needed
            chartPixelWidth: 0,
            chartPixelHeight: 0,
          };
        }

        // Parse pending operations
        const awaitingExecution: CodeRun[] = state.pending.map((op: CodeOperation) => ({
          transactionId: data.transactionId,
          sheetPos: { x: op.x, y: op.y, sheetId: op.sheet_id },
          code: '', // Code is not needed for display, can be retrieved from grid if needed
          chartPixelWidth: 0,
          chartPixelHeight: 0,
        }));

        // Native charts whose data depends on the running/pending cells (dimmed
        // while they wait for their re-laid-out display list).
        const updatingCharts = (state.charts ?? []).map((chart) => ({
          sheetId: chart.sheet_id,
          chartId: chart.chart_id,
        }));

        // Cache the latest state so callers can synchronously check whether the
        // code queue is idle without waiting for the next event.
        this._codeRunningState = { current, awaitingExecution };

        // Emit unified code running state
        events.emit('codeRunningState', current, awaitingExecution, updatingCharts);
      } catch (error) {
        console.error('Failed to parse code running state:', error);
      }
      return;
    } else if (e.data.type === 'coreClientPendingCellsState') {
      const cells = e.data.cells.map(
        (cell: { sheet_pos: { sheet_id: { id: string }; x: number; y: number }; value: string | null }) => ({
          sheetId: cell.sheet_pos.sheet_id.id,
          x: cell.sheet_pos.x,
          y: cell.sheet_pos.y,
          value: cell.value,
        })
      );
      events.emit('pendingCellsState', cells);
      return;
    } else if (e.data.type === 'coreClientOfflineTransactionStats') {
      events.emit('offlineTransactions', e.data.transactions, e.data.operations);
      return;
    } else if (e.data.type === 'coreClientOfflineTransactionsApplied') {
      events.emit('offlineTransactionsApplied', e.data.timestamps);
      return;
    } else if (e.data.type === 'coreClientUndoRedo') {
      events.emit('undoRedo', e.data.undo, e.data.redo);
      return;
    } else if (e.data.type === 'coreClientGetJwt') {
      // Skip the login redirect so anonymous users (public-link viewers and
      // embed mode) can still reach endpoints that accept anonymous traffic
      // (e.g. /financial). The SQL connection branch already handles an
      // empty jwt gracefully with an "Authentication required" stderr.
      const jwt = await authClient.getTokenOrRedirect(true);
      const data = e.data as CoreClientGetJwt;
      this.send({ type: 'clientCoreGetJwt', id: data.id, jwt });
      return;
    } else if (e.data.type === 'coreClientImage') {
      events.emit('updateImage', e.data);
      return;
    } else if (e.data.type === 'coreClientSheetMetaFills') {
      events.emit('sheetMetaFills', e.data.sheetId, fromUint8Array<JsSheetFill[]>(e.data.fills));
      return;
    } else if (e.data.type === 'coreClientSheetValidations') {
      const sheetValidations = fromUint8Array<Validation[]>(e.data.sheetValidations);
      events.emit('sheetValidations', e.data.sheetId, sheetValidations);
      return;
    } else if (e.data.type === 'coreClientValidationWarnings') {
      const warnings = fromUint8Array<JsHashValidationWarnings[]>(e.data.warnings);
      events.emit('validationWarnings', warnings);
      return;
    } else if (e.data.type === 'coreClientSheetConditionalFormats') {
      const conditionalFormats = fromUint8Array<ConditionalFormatClient[]>(e.data.conditionalFormats);
      events.emit('sheetConditionalFormats', e.data.sheetId, conditionalFormats);
      return;
    } else if (e.data.type === 'coreClientSheetComments') {
      const comments = fromUint8Array<CommentThread[]>(e.data.comments);
      events.emit('sheetComments', e.data.sheetId, comments);
      return;
    } else if (e.data.type === 'coreClientSheetCharts') {
      const charts = fromUint8Array<JsChart[]>(e.data.charts);
      events.emit('sheetCharts', e.data.sheetId, charts);
      return;
    } else if (e.data.type === 'coreClientMultiplayerSynced') {
      events.emit('multiplayerSynced');
      return;
    } else if (e.data.type === 'coreClientBordersSheet') {
      events.emit('bordersSheet', e.data.sheetId, fromUint8Array<JsBordersSheet>(e.data.borders));
      return;
    } else if (e.data.type === 'coreClientClientMessage') {
      gridSettings.snackbar(e.data.message, { severity: e.data.severity });

      // This is a hack to get import files to properly show negative offsets dialog
      // after importing from dashboard. This can be removed in the future.
      this.receivedClientMessage = true;

      return;
    } else if (e.data.type === 'coreClientA1Context') {
      events.emit('a1Context', e.data.context);
      return;
    } else if (e.data.type === 'coreClientRequestInitPython') {
      await pythonWebWorker.ensureInitialized();
      return;
    } else if (e.data.type === 'coreClientRequestInitJavascript') {
      try {
        await javascriptWebWorker.ensureInitialized();
      } catch (error) {
        console.error('[quadraticCore] Failed to initialize JavaScript worker:', error);
        // Emit error event so the core can handle it gracefully
        events.emit('coreError', 'javascriptWorker', error);
      }
      return;
    } else if (e.data.type === 'coreClientCoreError') {
      if (debugFlag('debug')) {
        console.error('[quadraticCore] core error', e.data.from, e.data.error);
      }
      events.emit('coreError', e.data.from, e.data.error);
      return;
    } else if (e.data.type === 'coreClientContentCache') {
      events.emit('contentCache', e.data.sheetId, new SheetContentCache(e.data.contentCache));
      return;
    } else if (e.data.type === 'coreClientDataTablesCache') {
      events.emit('dataTablesCache', e.data.sheetId, new SheetDataTablesCache(e.data.dataTablesCache));
      events.emit('dataTablesCacheRaw', e.data.sheetId, e.data.dataTablesCache);
      return;
    } else if (e.data.type === 'coreClientStartupTimer') {
      events.emit('startupTimer', e.data.name, { start: e.data.start, end: e.data.end });
      return;
    } else if (e.data.type === 'coreClientLoadProgress') {
      setFileLoadingProgress(e.data.loaded, e.data.total);
      return;
    } else if (e.data.type === 'coreClientWasmReady') {
      events.emit('coreWasmReady');
      return;
    } else if (e.data.type === 'coreClientWasmPerfMetrics') {
      events.emit('wasmPerfMetrics', e.data.metrics);
      return;
    } else if (e.data.type === 'coreClientMergeCells') {
      const data = e.data as CoreClientMergeCells;
      events.emit('mergeCells', data.sheetId, JsMergeCells.createFromBytes(data.mergeCells));
      return;
    }

    if (e.data.id !== undefined) {
      // handle responses from requests to quadratic-core
      if (this.waitingForResponse[e.data.id]) {
        this.waitingForResponse[e.data.id](e.data);
        delete this.waitingForResponse[e.data.id];
      } else {
        console.warn('No resolve for message in quadraticCore', e.data);
      }
    }

    // handle requests
    else {
      switch (e.data.type) {
        default:
          console.warn('Unhandled message type', e.data.type);
      }
    }
  };

  send(message: ClientCoreMessage, extra?: MessagePort | Transferable) {
    // worker may not be defined during hmr
    if (!this.worker) return;

    if (extra) {
      this.worker.postMessage(message, [extra]);
    } else {
      this.worker.postMessage(message);
    }
  }

  setTransactionProvenance(provenance?: TransactionProvenance) {
    this.transactionProvenance = provenance;
    this.send({
      type: 'clientCoreSetTransactionProvenance',
      provenance: provenance ? JSON.stringify(provenance) : undefined,
    });
  }

  /**
   * Perform a fetch on behalf of the core worker. Needed in native
   * (Electron) builds because Web Workers cannot reach the main-process
   * HTTP client — the main-thread `window.fetch` has been shimmed
   * (`src/app/nativeFetch.ts`) to route cross-origin requests through
   * Electron's `net.fetch`, which bypasses the CORS check that blocks
   * the `app://localhost` WebView origin.
   */
  private handleProxyFetch = async (request: CoreClientProxyFetch): Promise<void> => {
    const sendResponse = (response: ClientCoreProxyFetchResponse) => {
      if (!this.worker) return;
      if (response.body) {
        this.worker.postMessage(response, [response.body]);
      } else {
        this.worker.postMessage(response);
      }
    };

    try {
      // GET/HEAD must not carry a body. The worker-side proxy already strips
      // it, but enforce it here too so this doesn't depend on that ordering
      // (or on the native fetch shim) for correctness.
      const hasBodylessMethod = request.method === 'GET' || request.method === 'HEAD';
      const res = await fetch(request.url, {
        method: request.method,
        headers: request.headers,
        body: hasBodylessMethod ? undefined : (request.body ?? undefined),
      });
      const body = await res.arrayBuffer();
      sendResponse({
        type: 'clientCoreProxyFetchResponse',
        id: request.id,
        ok: res.ok,
        status: res.status,
        statusText: res.statusText,
        headers: Array.from(res.headers.entries()),
        body,
      });
    } catch (error) {
      sendResponse({
        type: 'clientCoreProxyFetchResponse',
        id: request.id,
        ok: false,
        status: 0,
        statusText: '',
        headers: [],
        error: error instanceof Error ? error.message : String(error),
      });
    }
  };

  // Loads a Grid file and initializes renderWebWorker upon response
  async load({
    fileId,
    teamUuid,
    url,
    version,
    sequenceNumber,
    noMultiplayer,
  }: {
    fileId: string;
    teamUuid: string;
    url: string;
    version: string;
    sequenceNumber: number;
    noMultiplayer: boolean;
  }): Promise<{ version?: string; error?: string }> {
    // Store teamUuid for use by Python worker and other components
    this._teamUuid = teamUuid;

    // this is the channel between the core worker and the render worker
    const port = new MessageChannel();
    renderWebWorker.init(port.port2);

    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientLoad) => {
        if (message.error) {
          if (debugFlag('debugShowFileIO')) console.log(`[quadraticCore] error loading file "${message.error}".`);
          resolve({ error: message.error });
        } else if (message.version) {
          if (debugFlag('debugShowFileIO')) console.log(`[quadraticCore] file loaded.`);
          if (this.transactionProvenance) {
            this.setTransactionProvenance(this.transactionProvenance);
          }
          resolve({ version: message.version });
        } else {
          throw new Error('Expected CoreClientLoad to include either version or error');
        }
      };
      // load the file and send the render message port to
      const message: ClientCoreLoad = {
        type: 'clientCoreLoad',
        url,
        version,
        sequenceNumber,
        id,
        fileId,
        teamUuid,
        noMultiplayer,
      };
      if (debugFlag('debugShowFileIO')) console.log(`[quadraticCore] loading file ${url}`);
      this.send(message, port.port1);
    });
  }

  async export(): Promise<Uint8Array> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientExport) => {
        resolve(message.grid);
      };
      this.send({ type: 'clientCoreExport', id });
    });
  }

  async exportExcel(): Promise<Uint8Array> {
    const id = this.id++;
    // resolve comment author names (user id -> display name) for the export
    const { getCommentAuthorNamesForExport } = await import('@/app/ui/menus/Comments/commentAuthorNames');
    const authorNames = await getCommentAuthorNamesForExport();
    return new Promise((resolve, reject) => {
      this.waitingForResponse[id] = (message: CoreClientExportExcel) => {
        // `core.exportExcel()` returns an empty buffer when the wasm export
        // throws. A real .xlsx is a zip archive and is never zero-length, so
        // treat empty as failure: without this the caller "succeeds" and hands
        // the user a 0-byte download instead of an error.
        if (message.excel.length === 0) {
          reject(new Error('Failed to export Excel'));
          return;
        }
        resolve(message.excel);
      };
      this.send({ type: 'clientCoreExportExcel', id, authorNames });
    });
  }

  /**
   * Page geometry for the Page Break Preview. Runs the same pagination as PDF
   * export, so the preview matches what prints.
   */
  getSheetPagination(sheetId: string): Promise<JsSheetPagination | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientGetSheetPagination) => {
        resolve(message.pagination);
      };
      this.send({ type: 'clientCoreGetSheetPagination', id, sheetId });
    });
  }

  /**
   * Exports the file as a PDF with Excel-parity print semantics.
   * `options` is a camelCase PdfExportOptions object; omit for defaults
   * (letter portrait, 100% zoom, all sheets).
   */
  async exportPdf(options?: Record<string, unknown>): Promise<Uint8Array> {
    const id = this.id++;
    return new Promise((resolve, reject) => {
      this.waitingForResponse[id] = (message: CoreClientExportPdf) => {
        if (message.pdf.length === 0) {
          reject(new Error('PDF export failed'));
        } else {
          resolve(message.pdf);
        }
      };
      this.send({ type: 'clientCoreExportPdf', id, options: JSON.stringify(options ?? {}) });
    });
  }

  async exportJson(): Promise<string> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientExportJson) => {
        resolve(message.json);
      };
      this.send({ type: 'clientCoreExportJson', id });
    });
  }

  // Gets a code cell from a sheet
  getCodeCell(sheetId: string, x: number, y: number): Promise<JsCodeCell | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      const message: ClientCoreGetCodeCell = {
        type: 'clientCoreGetCodeCell',
        sheetId,
        x,
        y,
        id,
      };
      this.waitingForResponse[id] = (message: CoreClientGetCodeCell) => {
        resolve(message.cell);
      };
      this.send(message);
    });
  }

  setCodeAISummary(sheetId: string, x: number, y: number, summary: string | undefined, code: string) {
    this.send({
      type: 'clientCoreSetCodeAISummary',
      sheetId,
      x,
      y,
      summary,
      code,
    });
  }

  stampCellSources(cellSources: CellSources) {
    this.send({
      type: 'clientCoreStampCellSources',
      cellSources: JSON.stringify(cellSources),
    });
  }

  getEditCell(sheetId: string, x: number, y: number): Promise<JsEditCell | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      const message: ClientCoreGetEditCell = {
        type: 'clientCoreGetEditCell',
        sheetId,
        x,
        y,
        id,
      };
      this.waitingForResponse[id] = (message: CoreClientGetEditCell) => {
        resolve(message.cell);
      };
      this.send(message);
    });
  }

  getDisplayCell(sheetId: string, x: number, y: number): Promise<string | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      const message: ClientCoreGetDisplayCell = {
        type: 'clientCoreGetDisplayCell',
        sheetId,
        x,
        y,
        id,
      };
      this.waitingForResponse[id] = (message: CoreClientGetDisplayCell) => {
        resolve(message.cell);
      };
      this.send(message);
    });
  }

  getCellValue(sheetId: string, x: number, y: number): Promise<JsCellValue | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientGetCellValue) => {
        resolve(message.value);
      };
      this.send({
        type: 'clientCoreGetCellValue',
        sheetId,
        x,
        y,
        id,
      });
    });
  }

  getAICells(
    selection: string,
    sheetId: string,
    page: number
  ): Promise<string | JsResponse | JsGetAICellResult | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientGetAICells) => {
        resolve(message.aiCells);
      };
      this.send({ type: 'clientCoreGetAICells', id, selection, sheetId, page });
    });
  }

  getAISelectionContexts(args: {
    selections: string[];
    maxRows: number | undefined;
  }): Promise<JsSummaryContext[] | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientGetAISelectionContexts) => {
        resolve(message.summaryContexts);
      };
      this.send({
        type: 'clientCoreGetAISelectionContexts',
        id,
        selections: args.selections,
        maxRows: args.maxRows,
      });
    });
  }

  setCellValue(sheetId: string, x: number, y: number, value: string, isAi: boolean) {
    this.send({
      type: 'clientCoreSetCellValue',
      sheetId,
      x,
      y,
      value,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  /**
   * Sets a cell to a RichText value with the given spans.
   * Each span can have: text, link, bold, italic, underline, strike_through, text_color, font_size
   */
  setCellRichText(sheetId: string, x: number, y: number, spans: TextSpan[]) {
    this.send({
      type: 'clientCoreSetCellRichText',
      sheetId,
      x,
      y,
      spansJson: JSON.stringify(spans),
      cursor: sheets.getCursorPosition(),
    });
  }

  /**
   * Sets a cell to an image value.
   * Creates a DataTable containing the image.
   * @param image - Either a base64 data URL or an external URL
   * @param isUrl - If true, stores as ImageUrl (external URL); if false, stores as Image (base64)
   * @param width - Optional width in cells
   * @param height - Optional height in cells
   */
  setCellImage(
    sheetId: string,
    x: number,
    y: number,
    image: string,
    isUrl: boolean,
    width?: number,
    height?: number
  ): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientSetCellImage) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreSetCellImage',
        id,
        sheetId,
        x,
        y,
        image,
        isUrl,
        width,
        height,
        cursor: sheets.getCursorPosition(),
      });
    });
  }

  setCellValues(sheetId: string, x: number, y: number, values: string[][], isAi: boolean) {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = () => {
        resolve(undefined);
      };
      this.send({
        type: 'clientCoreSetCellValues',
        id,
        sheetId,
        x,
        y,
        values,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  setCodeCellValue(options: {
    sheetId: string;
    x: number;
    y: number;
    language: CodeCellLanguage;
    codeString: string;
    codeCellName?: string;
    agentConnectionId?: string;
    isAi: boolean;
  }): Promise<string | undefined> {
    const id = this.id++;
    return new Promise((resolve, reject) => {
      this.waitingForResponse[id] = (message: CoreClientSetCodeCellValue) => {
        resolve(message.transactionId);
        if (message.error) {
          reject(new Error(message.error));
        }
      };
      this.send({
        type: 'clientCoreSetCodeCellValue',
        id,
        sheetId: options.sheetId,
        x: options.x,
        y: options.y,
        language: options.language,
        codeString: options.codeString,
        cursor: sheets.getCursorPosition(),
        codeCellName: options.codeCellName,
        agentConnectionId: options.agentConnectionId,
        isAi: options.isAi,
      });
    });
  }

  setFormula(options: { sheetId: string; selection: string; codeString: string }): Promise<string | undefined> {
    const id = this.id++;
    return new Promise((resolve, reject) => {
      this.waitingForResponse[id] = (message: CoreClientSetFormula) => {
        resolve(message.transactionId);
        if (message.error) {
          reject(new Error(message.error));
        }
      };
      this.send({
        type: 'clientCoreSetFormula',
        id,
        sheetId: options.sheetId,
        selection: options.selection,
        codeString: options.codeString,
        cursor: sheets.getCursorPosition(),
      });
    });
  }

  // Sets multiple formulas in a single transaction (batched)
  setFormulas(options: {
    sheetId: string;
    formulas: Array<{ selection: string; codeString: string }>;
  }): Promise<string | undefined> {
    const id = this.id++;
    return new Promise((resolve, reject) => {
      this.waitingForResponse[id] = (message: CoreClientSetFormulas) => {
        if (message.error) {
          reject(new Error(message.error));
        }
        resolve(message.transactionId);
      };
      // Convert to tuple format expected by Rust: [selection, code_string]
      const formulas: Array<[string, string]> = options.formulas.map((f) => [f.selection, f.codeString]);
      this.send({
        type: 'clientCoreSetFormulas',
        id,
        sheetId: options.sheetId,
        formulas,
        cursor: sheets.getCursorPosition(),
      });
    });
  }

  getCellFormatSummary(sheetId: string, x: number, y: number): Promise<CellFormatSummary> {
    const id = this.id++;
    return new Promise((resolve) => {
      const message: ClientCoreGetCellFormatSummary = {
        type: 'clientCoreGetCellFormatSummary',
        id,
        sheetId,
        x,
        y,
      };
      this.waitingForResponse[id] = (message: CoreClientGetCellFormatSummary) => {
        resolve(message.formatSummary);
      };
      this.send(message);
    });
  }

  async upgradeGridFile(
    grid: ArrayBuffer,
    sequenceNumber: number
  ): Promise<{
    contents?: ArrayBufferLike;
    version?: string;
    error?: string;
  }> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientUpgradeFile) => {
        resolve(message);
      };
      const message: ClientCoreUpgradeGridFile = {
        type: 'clientCoreUpgradeGridFile',
        grid,
        sequenceNumber,
        id,
      };
      this.send(message, grid);
    });
  }

  importFile = async (
    args: Omit<ClientCoreImportFile, 'type' | 'id'>
  ): Promise<Omit<CoreClientImportFile, 'type' | 'id'>> => {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientImportFile) => {
        resolve({
          contents: message.contents,
          version: message.version,
          error: message.error,
          responsePrompt: message.responsePrompt,
        });
      };
      this.send(
        {
          type: 'clientCoreImportFile',
          id,
          ...args,
        },
        args.file
      );
    });
  };

  /**
   * Whether importing this file at `location` would land on cells that already
   * hold content, so the caller can ask the user what to do about it.
   */
  importWouldCoverContent = async (args: Omit<ClientCoreImportWouldCoverContent, 'type' | 'id'>): Promise<boolean> => {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientImportWouldCoverContent) => {
        resolve(message.wouldCoverContent);
      };
      this.send({ type: 'clientCoreImportWouldCoverContent', id, ...args });
    });
  };

  initMultiplayer(port: MessagePort) {
    this.send({ type: 'clientCoreInitMultiplayer' }, port);
  }

  summarizeSelection(decimalPlaces: number, selection: string): Promise<JsSummarizeSelectionResult | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      const message: ClientCoreSummarizeSelection = {
        type: 'clientCoreSummarizeSelection',
        id,
        selection,
        decimalPlaces,
      };
      this.waitingForResponse[id] = (message: CoreClientSummarizeSelection) => {
        resolve(message.summary);
      };
      this.send(message);
    });
  }

  setBold(selection: string, bold: boolean | undefined, isAi: boolean) {
    this.send({
      type: 'clientCoreSetCellBold',
      selection,
      bold,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  setFillColor(selection: string, fillColor: string | undefined, isAi: boolean) {
    this.send({
      type: 'clientCoreSetCellFillColor',
      selection,
      fillColor,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  getRenderFillsForHashes(sheetId: string, hashes: JsCoordinate[]) {
    this.send({
      type: 'clientCoreGetRenderFillsForHashes',
      sheetId,
      hashes,
    });
  }

  getSheetMetaFills(sheetId: string) {
    this.send({
      type: 'clientCoreGetSheetMetaFills',
      sheetId,
    });
  }

  setItalic(selection: string, italic: boolean | undefined, isAi: boolean) {
    this.send({
      type: 'clientCoreSetCellItalic',
      selection,
      italic,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  setFontSize(selection: string, fontSize: number, isAi: boolean) {
    this.send({
      type: 'clientCoreSetCellFontSize',
      selection,
      fontSize,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  setTextColor(selection: string, color: string | undefined, isAi: boolean) {
    this.send({
      type: 'clientCoreSetCellTextColor',
      selection,
      color,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  setUnderline(selection: string, underline: boolean | undefined, isAi: boolean) {
    this.send({
      type: 'clientCoreSetCellUnderline',
      selection,
      underline,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  setStrikeThrough(selection: string, strikeThrough: boolean | undefined, isAi: boolean) {
    this.send({
      type: 'clientCoreSetCellStrikeThrough',
      selection,
      strikeThrough,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  setAlign(selection: string, align: CellAlign, isAi: boolean) {
    this.send({
      type: 'clientCoreSetCellAlign',
      selection,
      align,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  setVerticalAlign(selection: string, verticalAlign: CellVerticalAlign, isAi: boolean) {
    this.send({
      type: 'clientCoreSetCellVerticalAlign',
      selection,
      verticalAlign,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  setWrap(selection: string, wrap: CellWrap, isAi: boolean) {
    this.send({
      type: 'clientCoreSetCellWrap',
      selection,
      wrap,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  setCellCurrency(selection: string, symbol: string, isAi: boolean) {
    this.send({
      type: 'clientCoreSetCurrency',
      selection,
      symbol,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  setCellPercentage(selection: string, isAi: boolean) {
    this.send({
      type: 'clientCoreSetPercentage',
      selection,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  setCellExponential(selection: string, isAi: boolean) {
    this.send({
      type: 'clientCoreSetExponential',
      selection,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  removeNumericFormat(selection: string, isAi: boolean) {
    this.send({
      type: 'clientCoreRemoveCellNumericFormat',
      selection,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  changeDecimalPlaces(selection: string, delta: number, isAi: boolean) {
    this.send({
      type: 'clientCoreChangeDecimals',
      selection,
      delta,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  clearFormatting(selection: string, isAi: boolean) {
    this.send({
      type: 'clientCoreClearFormatting',
      selection,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  setCommas(selection: string, commas: boolean | undefined, isAi: boolean) {
    this.send({
      type: 'clientCoreSetCommas',
      selection,
      commas,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  setNegativeParens(selection: string, negativeParens: boolean | undefined, isAi: boolean) {
    this.send({
      type: 'clientCoreSetNegativeParens',
      selection,
      negativeParens,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  setNumericCustom(selection: string, code: string | undefined, isAi: boolean) {
    this.send({
      type: 'clientCoreSetNumericCustom',
      selection,
      code,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  setDateTimeFormat(selection: string, format: string, isAi: boolean) {
    this.send({
      type: 'clientCoreSetDateTimeFormat',
      selection,
      format,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  setFormats(
    sheetId: string,
    selection: string,
    formats: FormatUpdate,
    isAi: boolean
  ): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientSetFormats) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreSetFormats',
        id,
        sheetId,
        selection,
        formats,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  setFormatsA1(
    formatEntries: { sheetId: string; selection: string; formats: FormatUpdate }[],
    isAi: boolean
  ): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientSetFormatsA1) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreSetFormatsA1',
        id,
        formatEntries,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  getAICellFormats(sheetId: string, selection: string, page: number): Promise<string | JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientGetAIFormats) => {
        resolve(message.formats);
      };
      this.send({
        type: 'clientCoreGetAIFormats',
        id,
        sheetId,
        selection,
        page,
      });
    });
  }

  deleteCellValues(selection: string, isAi: boolean): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientDeleteCellValues) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreDeleteCellValues',
        id,
        selection,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  deleteValuesAndFormatting(selection: string, isAi: boolean): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientDeleteValuesAndFormatting) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreDeleteValuesAndFormatting',
        id,
        selection,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  search(search: string, searchOptions: SearchOptions): Promise<JsSearchResult[]> {
    const id = this.id++;
    return new Promise<JsSearchResult[]>((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientSearch) => {
        resolve(message.results);
      };
      this.send({
        type: 'clientCoreSearch',
        id,
        search,
        searchOptions,
      });
    });
  }

  /**
   * Replaces matches of `search` with `replacement` in a single undoable
   * transaction. When `positions` is provided, only matches at those cells are
   * replaced ("replace current" / selective replace).
   */
  searchReplace(
    search: string,
    replacement: string,
    searchOptions: SearchOptions,
    positions?: JsSheetPos[],
    isAi = false
  ): Promise<JsReplaceResult | undefined> {
    const id = this.id++;
    return new Promise<JsReplaceResult | undefined>((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientSearchReplace) => {
        resolve(message.result);
      };
      this.send({
        type: 'clientCoreSearchReplace',
        id,
        search,
        replacement,
        searchOptions,
        positions,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  neighborText(sheetId: string, x: number, y: number): Promise<string[]> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientNeighborText) => {
        resolve(message.text);
      };
      this.send({
        type: 'clientCoreNeighborText',
        id,
        sheetId,
        x,
        y,
      });
    });
  }

  rerunCodeCells(
    sheetId: string | undefined,
    selection: string | undefined,
    isAi: boolean
  ): Promise<string | JsResponse | undefined> {
    if (getIsEmbedMode() && !isAi) {
      let hasBlockedConnectionRun = false;

      if (sheetId && selection) {
        const cellsSheet = content.cellsSheets.getById(sheetId);
        hasBlockedConnectionRun =
          sheetId === sheets.current
            ? !!cellsSheet?.tables.hasConnectionCodeCellInCurrentSelection()
            : !!cellsSheet?.tables.hasAnyConnectionCodeCell();
      } else if (sheetId) {
        hasBlockedConnectionRun = !!content.cellsSheets.getById(sheetId)?.tables.hasAnyConnectionCodeCell();
      } else {
        hasBlockedConnectionRun = content.cellsSheets.children.some((cellsSheet) =>
          cellsSheet.tables.hasAnyConnectionCodeCell()
        );
      }

      if (hasBlockedConnectionRun) {
        gridSettings.snackbar(
          'Connection code cells cannot be run in embeds. Open this file in Quadratic to run connections.',
          {
            severity: 'error',
          }
        );
        return Promise.resolve(undefined);
      }
    }

    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientRerunCodeCells) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreRerunCodeCells',
        id,
        sheetId,
        selection,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  //#region Sheet Operations

  addSheet(
    sheetName: string | undefined,
    insertBeforeSheetName: string | undefined,
    isAi: boolean
  ): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientAddSheetResponse) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreAddSheet',
        id,
        sheetName,
        insertBeforeSheetName,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  duplicateSheet(sheetId: string, nameOfNewSheet: string | undefined, isAi: boolean): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientDuplicateSheetResponse) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreDuplicateSheet',
        id,
        sheetId,
        nameOfNewSheet,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  deleteSheet(sheetId: string, isAi: boolean): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientDeleteSheetResponse) => {
        resolve(message.response);
      };
      this.send({ type: 'clientCoreDeleteSheet', id, sheetId, cursor: sheets.getCursorPosition(), isAi });
    });
  }

  moveSheet(sheetId: string, previous: string | undefined, isAi: boolean): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientMoveSheetResponse) => {
        resolve(message.response);
      };
      this.send({ type: 'clientCoreMoveSheet', id, sheetId, previous, cursor: sheets.getCursorPosition(), isAi });
    });
  }

  setSheetName(sheetId: string, name: string, isAi: boolean): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientSetSheetNameResponse) => {
        resolve(message.response);
      };
      this.send({ type: 'clientCoreSetSheetName', id, sheetId, name, cursor: sheets.getCursorPosition(), isAi });
    });
  }

  setDefinedName(args: {
    name: string;
    scope?: string;
    newName: string;
    newScope?: string;
    formula: string;
    comment?: string;
    isAi: boolean;
  }): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientSetDefinedNameResponse) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreSetDefinedName',
        id,
        ...args,
        cursor: sheets.getCursorPosition(),
      });
    });
  }

  deleteDefinedName(name: string, scope: string | undefined, isAi: boolean): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientDeleteDefinedNameResponse) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreDeleteDefinedName',
        id,
        name,
        scope,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  setSheetColor(sheetId: string, color: string | undefined, isAi: boolean): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientSetSheetColorResponse) => {
        resolve(message.response);
      };
      this.send({ type: 'clientCoreSetSheetColor', id, sheetId, color, cursor: sheets.getCursorPosition(), isAi });
    });
  }

  setSheetFrozen(
    sheetId: string,
    frozenRows: number,
    frozenColumns: number,
    isAi: boolean
  ): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientSetSheetFrozenResponse) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreSetSheetFrozen',
        id,
        sheetId,
        frozenRows,
        frozenColumns,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  setSheetPrintSetup(
    sheetId: string,
    printSetup: SheetPrintSetupPayload,
    isAi: boolean
  ): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientSetSheetPrintSetupResponse) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreSetSheetPrintSetup',
        id,
        sheetId,
        printSetup: JSON.stringify(printSetup),
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  setSheetsColor(sheetNameToColor: JsSheetNameToColor[], isAi: boolean): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientSetSheetsColorResponse) => {
        resolve(message.response);
      };
      this.send({ type: 'clientCoreSetSheetsColor', id, sheetNameToColor, cursor: sheets.getCursorPosition(), isAi });
    });
  }

  //#endregion

  //#region Undo/redo

  undo(count: number, isAi: boolean): Promise<UndoRedoResponse | undefined> {
    return new Promise((resolve) => {
      const id = this.id++;
      this.waitingForResponse[id] = (message: CoreClientUndoResponse) => {
        resolve(message.response);
      };
      this.send({ type: 'clientCoreUndo', id, count, cursor: sheets.getCursorPosition(), isAi });
    });
  }

  redo(count: number, isAi: boolean): Promise<UndoRedoResponse | undefined> {
    return new Promise((resolve) => {
      const id = this.id++;
      this.waitingForResponse[id] = (message: CoreClientRedoResponse) => {
        resolve(message.response);
      };
      this.send({ type: 'clientCoreRedo', id, count, cursor: sheets.getCursorPosition(), isAi });
    });
  }

  //#endregion

  //#region Clipboard

  copyToClipboard(selection: string): Promise<JsClipboard> {
    return new Promise((resolve) => {
      const id = this.id++;
      this.waitingForResponse[id] = (message: CoreClientCopyToClipboard) => {
        let jsClipboard = {} as JsClipboard;
        if (message.data) {
          jsClipboard = fromUint8Array<JsClipboard>(message.data);
        }
        resolve(jsClipboard);
      };
      this.send({
        type: 'clientCoreCopyToClipboard',
        id,
        selection,
      });
    });
  }

  cutToClipboard(selection: string, isAi: boolean): Promise<JsClipboard> {
    return new Promise((resolve) => {
      const id = this.id++;
      this.waitingForResponse[id] = (message: CoreClientCutToClipboard) => {
        let jsClipboard = {} as JsClipboard;
        if (message.data) {
          jsClipboard = fromUint8Array<JsClipboard>(message.data);
        }
        resolve(jsClipboard);
      };
      this.send({
        type: 'clientCoreCutToClipboard',
        id,
        selection,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  pasteFromClipboard(options: { selection: string; jsClipboard: Uint8Array; special: PasteSpecial; isAi: boolean }) {
    const { selection, jsClipboard, special, isAi } = options;
    this.send(
      {
        type: 'clientCorePasteFromClipboard',
        selection,
        jsClipboard,
        special,
        cursor: sheets.getCursorPosition(),
        isAi,
      },
      jsClipboard.buffer
    );
  }

  applyFormatPainter(sourceSelection: string, targetSelection: string, isAi: boolean) {
    this.send({
      type: 'clientCoreApplyFormatPainter',
      sourceSelection,
      targetSelection,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  //#endregion

  //#region Borders

  setBorders(
    selection: string,
    borderSelection: BorderSelection,
    style: BorderStyle | undefined,
    isAi: boolean
  ): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientSetBorders) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreSetBorders',
        id,
        selection,
        borderSelection,
        style,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  mergeCells(selection: string, isAi: boolean): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientMergeCellsResponse) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreMergeCells',
        id,
        selection,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  unmergeCells(selection: string, isAi: boolean): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientUnmergeCellsResponse) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreUnmergeCells',
        id,
        selection,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  mergeCellsBatch(selections: string[], isAi: boolean): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientMergeCellsBatchResponse) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreMergeCellsBatch',
        id,
        selections,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  unmergeCellsBatch(selections: string[], isAi: boolean): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientUnmergeCellsBatchResponse) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreUnmergeCellsBatch',
        id,
        selections,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  //#endregion

  //#region Misc.

  setChartSize(
    sheetId: string,
    x: number,
    y: number,
    width: number,
    height: number,
    isAi: boolean
  ): Promise<JsResponse | undefined> {
    return new Promise((resolve) => {
      const id = this.id++;
      this.waitingForResponse[id] = (message: CoreClientSetCellRenderResize) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreSetCellRenderResize',
        id,
        sheetId,
        x,
        y,
        width,
        height,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  autocomplete(
    sheetId: string,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    fullX1: number,
    fullY1: number,
    fullX2: number,
    fullY2: number,
    isAi: boolean
  ) {
    this.send({
      type: 'clientCoreAutocomplete',
      sheetId,
      x1,
      y1,
      x2,
      y2,
      fullX1,
      fullY1,
      fullX2,
      fullY2,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  exportCsvSelection(selection: string): Promise<string> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientExportCsvSelection) => {
        resolve(message.csv);
      };
      return this.send({
        type: 'clientCoreExportCsvSelection',
        id,
        selection,
      });
    });
  }

  exportParquetSelection(selection: string): Promise<Uint8Array> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientExportParquetSelection) => {
        resolve(message.parquet);
      };
      return this.send({
        type: 'clientCoreExportParquetSelection',
        id,
        selection,
      });
    });
  }

  moveColsRows(
    source: SheetRect,
    targetX: number,
    targetY: number,
    targetSheetId: string,
    columns: boolean,
    rows: boolean,
    isAi: boolean
  ) {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = () => {
        resolve(undefined);
      };
      this.send({
        type: 'clientCoreMoveColsRows',
        id,
        source,
        targetSheetId,
        targetX,
        targetY,
        columns,
        rows,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  // Move multiple cell regions in a single transaction
  moveCellsBatch(
    moves: { source: SheetRect; targetX: number; targetY: number; targetSheetId: string }[],
    isAi: boolean
  ) {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = () => {
        resolve(undefined);
      };
      this.send({
        type: 'clientCoreMoveCellsBatch',
        id,
        moves: moves.map((m) => ({
          source: m.source,
          dest: { x: m.targetX, y: m.targetY, sheet_id: { id: m.targetSheetId } },
        })),
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  moveCodeCellVertically(args: {
    sheetId: string;
    x: number;
    y: number;
    sheetEnd: boolean;
    reverse: boolean;
    isAi: boolean;
  }): Promise<Pos | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientMoveCodeCellVertically) => {
        resolve(message.pos);
      };
      this.send({
        type: 'clientCoreMoveCodeCellVertically',
        id,
        sheetId: args.sheetId,
        x: args.x,
        y: args.y,
        sheetEnd: args.sheetEnd,
        reverse: args.reverse,
        cursor: sheets.getCursorPosition(),
        isAi: args.isAi,
      });
    });
  }

  moveCodeCellHorizontally(args: {
    sheetId: string;
    x: number;
    y: number;
    sheetEnd: boolean;
    reverse: boolean;
    isAi: boolean;
  }): Promise<Pos | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientMoveCodeCellHorizontally) => {
        resolve(message.pos);
      };
      return this.send({
        type: 'clientCoreMoveCodeCellHorizontally',
        id,
        sheetId: args.sheetId,
        x: args.x,
        y: args.y,
        sheetEnd: args.sheetEnd,
        reverse: args.reverse,
        cursor: sheets.getCursorPosition(),
        isAi: args.isAi,
      });
    });
  }

  //#endregion

  //#region Bounds

  commitTransientResize(sheetId: string, transientResize: string, isAi: boolean) {
    this.send({
      type: 'clientCoreCommitTransientResize',
      sheetId,
      transientResize,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  commitSingleResize(
    sheetId: string,
    column: number | undefined,
    row: number | undefined,
    size: number,
    isAi: boolean
  ) {
    this.send({
      type: 'clientCoreCommitSingleResize',
      sheetId,
      column,
      row,
      size,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  //#endregion

  //#region Calculation

  sendInit() {
    this.send({
      type: 'clientCoreInit',
      env: import.meta.env,
      isEmbedMode: getIsEmbedMode(),
      isNative: isNative(),
    });
  }

  sendPythonInit(port: MessagePort) {
    this.send({ type: 'clientCoreInitPython' }, port);
  }

  sendJavascriptInit(port: MessagePort) {
    this.send({ type: 'clientCoreInitJavascript' }, port);
  }

  sendCancelExecution(language: CodeCellLanguage) {
    this.send({ type: 'clientCoreCancelExecution', language });
  }

  sendCancelConnectionExecutions() {
    this.send({ type: 'clientCoreCancelConnectionExecutions' });
  }

  clearAllPendingExecutions() {
    this.send({ type: 'clientCoreClearAllPendingExecutions' });
  }

  //#endregion

  //#region Data Validation

  getValidationFromPos(sheetId: string, x: number, y: number): Promise<Validation | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientGetValidationFromPos) => {
        resolve(message.validation);
      };
      this.send({
        type: 'clientCoreGetValidationFromPos',
        id,
        sheetId,
        x,
        y,
      });
    });
  }

  getValidations(sheetId: string): Promise<Validation[]> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientGetValidations) => {
        resolve(message.validations);
      };
      this.send({
        type: 'clientCoreGetValidations',
        id,
        sheetId,
      });
    });
  }

  updateCommentThread(sheetId: string, thread: CommentThread, isAi: boolean): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientUpdateCommentThread) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreUpdateCommentThread',
        id,
        sheetId,
        thread,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  removeCommentThread(sheetId: string, threadId: string, isAi: boolean) {
    this.send({
      type: 'clientCoreRemoveCommentThread',
      sheetId,
      threadId,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  updateValidation(validation: ValidationUpdate, isAi: boolean): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientUpdateValidation) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreUpdateValidation',
        id,
        validation,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  removeValidation(sheetId: string, validationId: string, isAi: boolean) {
    this.send({
      type: 'clientCoreRemoveValidation',
      sheetId,
      validationId,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  removeValidationSelection(sheetId: string, selection: string, isAi: boolean): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientRemoveValidationSelection) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreRemoveValidationSelection',
        id,
        sheetId,
        selection,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  removeValidations(sheetId: string, isAi: boolean) {
    this.send({
      type: 'clientCoreRemoveValidations',
      sheetId,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  updateChart(sheetId: string, chartUpdate: ChartUpdate, isAi: boolean): Promise<{ chartId?: string; error?: string }> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientUpdateChart) => {
        resolve({ chartId: message.chartId, error: message.error });
      };
      this.send({
        type: 'clientCoreUpdateChart',
        id,
        sheetId,
        chartUpdate,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  removeChart(sheetId: string, chartId: string, isAi: boolean) {
    this.send({
      type: 'clientCoreRemoveChart',
      sheetId,
      chartId,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  //#region Pivot tables

  createPivotTable(
    sheetId: string,
    x: number,
    y: number,
    source: PivotSource,
    name: string | undefined,
    isAi: boolean
  ): Promise<{ spec?: PivotSpec; error?: string }> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientCreatePivotTable) => {
        resolve({ spec: message.spec, error: message.error });
      };
      this.send({
        type: 'clientCoreCreatePivotTable',
        id,
        sheetId,
        x,
        y,
        source,
        name,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  createPivotTableOnNewSheet(
    source: PivotSource,
    spec: PivotSpec | undefined,
    name: string | undefined,
    isAi: boolean
  ): Promise<{ sheetId?: string; tableName?: string; spec?: PivotSpec; error?: string }> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientCreatePivotTableOnNewSheet) => {
        resolve({
          sheetId: message.sheetId,
          tableName: message.tableName,
          spec: message.spec,
          error: message.error,
        });
      };
      this.send({
        type: 'clientCoreCreatePivotTableOnNewSheet',
        id,
        source,
        spec,
        name,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  getPivotSourceFields(
    sheetId: string,
    source: PivotSource
  ): Promise<{ fields?: JsPivotSourceField[]; error?: string }> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientGetPivotSourceFields) => {
        resolve({ fields: message.fields, error: message.error });
      };
      this.send({ type: 'clientCoreGetPivotSourceFields', id, sheetId, source });
    });
  }

  getPivotFieldItems(
    sheetId: string,
    x: number,
    y: number,
    columnsArea: boolean,
    fieldIndex: number
  ): Promise<{ items?: JsPivotFieldItem[]; error?: string }> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientGetPivotFieldItems) => {
        resolve({ items: message.items, error: message.error });
      };
      this.send({ type: 'clientCoreGetPivotFieldItems', id, sheetId, x, y, columnsArea, fieldIndex });
    });
  }

  setPivotTable(
    sheetId: string,
    x: number,
    y: number,
    spec: PivotSpec,
    name: string | undefined,
    isAi: boolean
  ): Promise<{ tableName?: string; error?: string }> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientSetPivotTable) => {
        resolve({ tableName: message.tableName, error: message.error });
      };
      this.send({
        type: 'clientCoreSetPivotTable',
        id,
        sheetId,
        x,
        y,
        spec,
        name,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  deletePivotTable(sheetId: string, x: number, y: number, isAi: boolean) {
    this.send({
      type: 'clientCoreDeletePivotTable',
      sheetId,
      x,
      y,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  getPivotSpec(sheetId: string, x: number, y: number): Promise<PivotSpec | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientGetPivotSpec) => {
        resolve(message.spec);
      };
      this.send({ type: 'clientCoreGetPivotSpec', id, sheetId, x, y });
    });
  }

  /**
   * Locates the pivot report named `name`. Read live from core, so it reflects
   * a report that was just created or reshaped.
   */
  getPivotTableByName(name: string): Promise<PivotTableLocation | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientGetPivotTableByName) => {
        resolve(message.pivotTable);
      };
      this.send({ type: 'clientCoreGetPivotTableByName', id, name });
    });
  }

  togglePivotCollapse(sheetId: string, x: number, y: number) {
    this.send({
      type: 'clientCoreTogglePivotCollapse',
      sheetId,
      x,
      y,
      cursor: sheets.getCursorPosition(),
    });
  }

  pivotDrillToDetail(sheetId: string, x: number, y: number): Promise<{ sheetId?: string; error?: string }> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientPivotDrillToDetail) => {
        resolve({ sheetId: message.sheetId, error: message.error });
      };
      this.send({
        type: 'clientCorePivotDrillToDetail',
        id,
        sheetId,
        x,
        y,
        cursor: sheets.getCursorPosition(),
      });
    });
  }

  //#endregion

  duplicateChart(sheetId: string, chartId: string): Promise<string | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientDuplicateChart) => {
        resolve(message.chartId);
      };
      this.send({ type: 'clientCoreDuplicateChart', id, sheetId, chartId });
    });
  }

  getChartUpdate(sheetId: string, chartId: string): Promise<ChartUpdate | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientGetChartUpdate) => {
        resolve(message.chartUpdate);
      };
      this.send({ type: 'clientCoreGetChartUpdate', id, sheetId, chartId });
    });
  }

  copyChartToClipboard(sheetId: string, chartId: string): Promise<JsClipboard> {
    return new Promise((resolve) => {
      const id = this.id++;
      this.waitingForResponse[id] = (message: CoreClientCopyChartToClipboard) => {
        let jsClipboard = {} as JsClipboard;
        if (message.data) {
          jsClipboard = fromUint8Array<JsClipboard>(message.data);
        }
        resolve(jsClipboard);
      };
      this.send({ type: 'clientCoreCopyChartToClipboard', id, sheetId, chartId });
    });
  }

  getChartUpdateFromSelection(selection: string): Promise<ChartUpdate | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientGetChartUpdateFromSelection) => {
        resolve(message.chartUpdate);
      };
      this.send({ type: 'clientCoreGetChartUpdateFromSelection', id, selection });
    });
  }

  getChartUpdatePreview(sheetId: string, chartUpdate: ChartUpdate): Promise<JsChart | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientGetChartUpdatePreview) => {
        resolve(message.chart);
      };
      this.send({ type: 'clientCoreGetChartUpdatePreview', id, sheetId, chartUpdate });
    });
  }

  getChartPreview(sheetId: string, chartId: string, w: number, h: number): Promise<JsChartLayout | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientGetChartPreview) => {
        resolve(message.preview);
      };
      this.send({ type: 'clientCoreGetChartPreview', id, sheetId, chartId, w, h });
    });
  }

  setChartRect(sheetId: string, chartId: string, x: number, y: number, w: number, h: number) {
    this.send({
      type: 'clientCoreSetChartRect',
      sheetId,
      chartId,
      x,
      y,
      w,
      h,
      cursor: sheets.getCursorPosition(),
    });
  }

  updateConditionalFormat(conditionalFormat: ConditionalFormatUpdate): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientUpdateConditionalFormat) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreUpdateConditionalFormat',
        id,
        conditionalFormat,
        cursor: sheets.getCursorPosition(),
      });
    });
  }

  removeConditionalFormat(sheetId: string, conditionalFormatId: string) {
    this.send({
      type: 'clientCoreRemoveConditionalFormat',
      sheetId,
      conditionalFormatId,
      cursor: sheets.getCursorPosition(),
    });
  }

  batchUpdateConditionalFormats(
    sheetId: string,
    updates: ConditionalFormatUpdate[],
    deleteIds: string[]
  ): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientBatchUpdateConditionalFormats) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreBatchUpdateConditionalFormats',
        id,
        sheetId,
        updates,
        deleteIds,
        cursor: sheets.getCursorPosition(),
      });
    });
  }

  previewConditionalFormat(conditionalFormat: ConditionalFormatUpdate): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientPreviewConditionalFormat) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCorePreviewConditionalFormat',
        id,
        conditionalFormat,
      });
    });
  }

  clearPreviewConditionalFormat(sheetId: string) {
    this.send({
      type: 'clientCoreClearPreviewConditionalFormat',
      sheetId,
    });
  }

  getValidationList(sheetId: string, x: number, y: number): Promise<string[] | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientGetValidationList) => {
        resolve(message.validations);
      };
      this.send({
        type: 'clientCoreGetValidationList',
        id,
        sheetId,
        x,
        y,
      });
    });
  }

  validateInput(sheetId: string, x: number, y: number, input: string): Promise<string | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientValidateInput) => {
        resolve(message.validationId);
      };
      this.send({
        type: 'clientCoreValidateInput',
        id,
        sheetId,
        x,
        y,
        input,
      });
    });
  }

  //#endregion
  //#region manipulate columns and rows

  deleteColumns(sheetId: string, columns: number[], isAi: boolean): Promise<JsResponse | undefined> {
    shiftCellDocumentSources(sheetId, 'x', columns);
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientDeleteColumns) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreDeleteColumns',
        id,
        sheetId,
        columns,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  insertColumns(
    sheetId: string,
    column: number,
    count: number,
    right: boolean,
    isAi: boolean
  ): Promise<JsResponse | undefined> {
    cellDocumentSources.shift({ sheetId, axis: 'x', at: right ? column + 1 : column, delta: count });
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientInsertColumns) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreInsertColumns',
        id,
        sheetId,
        column,
        count,
        right,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  deleteRows(sheetId: string, rows: number[], isAi: boolean): Promise<JsResponse | undefined> {
    shiftCellDocumentSources(sheetId, 'y', rows);
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientDeleteRows) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreDeleteRows',
        id,
        sheetId,
        rows,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  insertRows(
    sheetId: string,
    row: number,
    count: number,
    below: boolean,
    isAi: boolean
  ): Promise<JsResponse | undefined> {
    cellDocumentSources.shift({ sheetId, axis: 'y', at: below ? row + 1 : row, delta: count });
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientInsertRows) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreInsertRows',
        id,
        sheetId,
        row,
        count,
        below,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  moveColumns(sheetId: string, colStart: number, colEnd: number, to: number, isAi: boolean) {
    this.send({
      type: 'clientCoreMoveColumns',
      sheetId,
      colStart,
      colEnd,
      to,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  moveRows(sheetId: string, rowStart: number, rowEnd: number, to: number, isAi: boolean) {
    this.send({
      type: 'clientCoreMoveRows',
      sheetId,
      rowStart,
      rowEnd,
      to,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  //#endregion
  //#region data tables

  flattenDataTable(sheetId: string, x: number, y: number, isAi: boolean) {
    this.send({
      type: 'clientCoreFlattenDataTable',
      sheetId,
      x,
      y,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  codeDataTableToDataTable(sheetId: string, x: number, y: number, isAi: boolean) {
    this.send({
      type: 'clientCoreCodeDataTableToDataTable',
      sheetId,
      x,
      y,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  gridToDataTable(
    sheetRect: string,
    tableName: string | undefined,
    firstRowIsHeader: boolean,
    isAi: boolean
  ): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientGridToDataTable) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreGridToDataTable',
        id,
        sheetRect,
        firstRowIsHeader,
        tableName,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  dataTableMeta(
    sheetId: string,
    x: number,
    y: number,
    options: {
      name?: string;
      alternatingColors?: boolean;
      columns?: JsDataTableColumnHeader[];
      showName?: boolean;
      showColumns?: boolean;
    },
    isAi: boolean
  ): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientDataTableMeta) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreDataTableMeta',
        id,
        sheetId,
        x,
        y,
        name: options.name,
        alternatingColors: options.alternatingColors,
        columns: options.columns,
        showName: options.showName,
        showColumns: options.showColumns,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  dataTableMutations(args: {
    sheetId: string;
    x: number;
    y: number;
    select_table: boolean;
    columns_to_add?: number[];
    columns_to_remove?: number[];
    rows_to_add?: number[];
    rows_to_remove?: number[];
    flatten_on_delete?: boolean;
    swallow_on_insert?: boolean;
    isAi: boolean;
  }) {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = () => {
        resolve(undefined);
      };
      this.send({
        type: 'clientCoreDataTableMutations',
        id,
        sheetId: args.sheetId,
        x: args.x,
        y: args.y,
        select_table: args.select_table,
        columns_to_add: args.columns_to_add,
        columns_to_remove: args.columns_to_remove,
        rows_to_add: args.rows_to_add,
        rows_to_remove: args.rows_to_remove,
        flatten_on_delete: args.flatten_on_delete,
        swallow_on_insert: args.swallow_on_insert,
        cursor: sheets.getCursorPosition(),
        isAi: args.isAi,
      });
    });
  }

  sortDataTable(sheetId: string, x: number, y: number, sort: DataTableSort[] | undefined, isAi: boolean) {
    this.send({
      type: 'clientCoreSortDataTable',
      sheetId,
      x,
      y,
      sort,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  filterDataTableColumn(
    sheetId: string,
    x: number,
    y: number,
    columnIndex: number,
    hiddenValues: unknown[],
    isAi: boolean
  ) {
    this.send({
      type: 'clientCoreFilterDataTableColumn',
      sheetId,
      x,
      y,
      columnIndex,
      hiddenValues,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  /// Clears every column's filter of a data table as a single transaction
  /// (one undo step); resolves when core has applied it.
  clearDataTableFilters(sheetId: string, x: number, y: number, isAi: boolean): Promise<{ error?: string }> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientClearDataTableFilters) => {
        resolve({ error: message.error });
      };
      this.send({
        type: 'clientCoreClearDataTableFilters',
        id,
        sheetId,
        x,
        y,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  filterDataTableColumnCondition(
    sheetId: string,
    x: number,
    y: number,
    columnIndex: number,
    condition: JsColumnFilterCondition | null,
    isAi: boolean
  ): Promise<{ error?: string }> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientFilterDataTableColumnCondition) => {
        resolve({ error: message.error });
      };
      this.send({
        type: 'clientCoreFilterDataTableColumnCondition',
        id,
        sheetId,
        x,
        y,
        columnIndex,
        condition,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  getDataTableColumnFilterInfo(
    sheetId: string,
    x: number,
    y: number,
    columnIndex: number,
    includeItems: boolean
  ): Promise<{ info?: JsColumnFilterInfo; error?: string }> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientGetDataTableColumnFilterInfo) => {
        resolve({ info: message.info, error: message.error });
      };
      this.send({ type: 'clientCoreGetDataTableColumnFilterInfo', id, sheetId, x, y, columnIndex, includeItems });
    });
  }

  toggleSheetAutoFilter(sheetId: string, selection: string, isAi: boolean) {
    this.send({
      type: 'clientCoreToggleSheetAutoFilter',
      sheetId,
      selection,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  clearSheetAutoFilter(sheetId: string, isAi: boolean) {
    this.send({
      type: 'clientCoreClearSheetAutoFilter',
      sheetId,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  reapplySheetAutoFilter(sheetId: string, isAi: boolean) {
    this.send({
      type: 'clientCoreReapplySheetAutoFilter',
      sheetId,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  filterSheetColumn(sheetId: string, columnIndex: number, hiddenValues: unknown[], isAi: boolean) {
    this.send({
      type: 'clientCoreFilterSheetColumn',
      sheetId,
      columnIndex,
      hiddenValues,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  filterSheetColumnCondition(
    sheetId: string,
    columnIndex: number,
    condition: JsColumnFilterCondition | null,
    isAi: boolean
  ): Promise<{ error?: string }> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientFilterSheetColumnCondition) => {
        resolve({ error: message.error });
      };
      this.send({
        type: 'clientCoreFilterSheetColumnCondition',
        id,
        sheetId,
        columnIndex,
        condition,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  sortSheetAutoFilter(sheetId: string, columnIndex: number, descending: boolean, isAi: boolean) {
    this.send({
      type: 'clientCoreSortSheetAutoFilter',
      sheetId,
      columnIndex,
      descending,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  getSheetColumnFilterInfo(
    sheetId: string,
    columnIndex: number,
    includeItems: boolean
  ): Promise<{ info?: JsColumnFilterInfo; error?: string }> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientGetSheetColumnFilterInfo) => {
        resolve({ info: message.info, error: message.error });
      };
      this.send({ type: 'clientCoreGetSheetColumnFilterInfo', id, sheetId, columnIndex, includeItems });
    });
  }

  dataTableFirstRowAsHeader(
    sheetId: string,
    x: number,
    y: number,
    firstRowAsHeader: boolean,
    isAi: boolean
  ): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientDataTableFirstRowAsHeader) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreDataTableFirstRowAsHeader',
        id,
        sheetId,
        x,
        y,
        firstRowAsHeader,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  addDataTable(args: {
    sheetId: string;
    x: number;
    y: number;
    name: string;
    values: string[][];
    firstRowIsHeader: boolean;
    isAi: boolean;
  }) {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = () => {
        resolve(undefined);
      };
      this.send({
        type: 'clientCoreAddDataTable',
        id,
        sheetId: args.sheetId,
        x: args.x,
        y: args.y,
        name: args.name,
        values: args.values,
        firstRowIsHeader: args.firstRowIsHeader,
        cursor: sheets.getCursorPosition(),
        isAi: args.isAi,
      });
    });
  }
  //#endregion

  resizeColumns(sheetId: string, columns: ColumnRowResize[], isAi: boolean): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientResizeColumns) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreResizeColumns',
        id,
        sheetId,
        columns,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  resizeRows(
    sheetId: string,
    rows: ColumnRowResize[],
    isAi: boolean,
    clientResized: boolean = true
  ): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientResizeColumns) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreResizeRows',
        id,
        sheetId,
        rows,
        cursor: sheets.getCursorPosition(),
        isAi,
        clientResized,
      });
    });
  }

  hideColumns(sheetId: string, columns: number[], hide: boolean, isAi: boolean): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientHideColumns) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreHideColumns',
        id,
        sheetId,
        columns,
        hide,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  hideRows(sheetId: string, rows: number[], hide: boolean, isAi: boolean): Promise<JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientHideRows) => {
        resolve(message.response);
      };
      this.send({
        type: 'clientCoreHideRows',
        id,
        sheetId,
        rows,
        hide,
        cursor: sheets.getCursorPosition(),
        isAi,
      });
    });
  }

  resizeAllColumns(sheetId: string, size: number, isAi: boolean) {
    this.send({
      type: 'clientCoreResizeAllColumns',
      sheetId,
      size,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  resizeAllRows(sheetId: string, size: number, isAi: boolean) {
    this.send({
      type: 'clientCoreResizeAllRows',
      sheetId,
      size,
      cursor: sheets.getCursorPosition(),
      isAi,
    });
  }

  getFormatSelection(selection: string): Promise<CellFormatSummary | JsResponse | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientGetFormatSelection) => {
        resolve(message.format);
      };
      this.send({
        type: 'clientCoreGetFormatSelection',
        id,
        selection,
      });
    });
  }

  hasCellData(sheetId: string, selection: string): Promise<boolean> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientHasCellData) => {
        resolve(message.hasData);
      };
      this.send({
        type: 'clientCoreHasCellData',
        sheetId,
        selection,
        id,
      });
    });
  }

  getAICodeErrors(maxErrors: number): Promise<Map<string, JsCodeErrorContext[]> | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientGetAICodeErrors) => {
        resolve(message.errors);
      };
      this.send({
        type: 'clientCoreGetAICodeErrors',
        id,
        maxErrors,
      });
    });
  }

  getAITransactions(): Promise<TrackedTransaction[] | undefined> {
    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = (message: CoreClientGetAITransactions) => {
        resolve(message.transactions);
      };
      this.send({
        type: 'clientCoreGetAITransactions',
        id,
      });
    });
  }

  /**
   * Update the viewport cache in the core worker.
   * Used when SharedArrayBuffer is not available.
   */
  updateViewport(topLeft: Pos, bottomRight: Pos, sheetId: string): void {
    this.send({
      type: 'clientCoreViewportUpdate',
      topLeft,
      bottomRight,
      sheetId,
    });
  }

  /**
   * Resets the core worker for loading a new file without terminating the worker.
   * This allows switching between files without a full page reload.
   */
  async reset(): Promise<void> {
    if (!this.worker) return;

    const id = this.id++;
    return new Promise((resolve) => {
      this.waitingForResponse[id] = () => {
        this._teamUuid = undefined;
        this.receivedClientMessage = false;
        this.waitingForResponse = {};
        resolve();
      };
      this.send({
        type: 'clientCoreReset',
        id,
      });
    });
  }

  /**
   * Terminates the core worker and cleans up resources.
   * Use this when completely unloading the application.
   */
  terminate(): void {
    if (this.worker) {
      wasmPerfReporter.stop();
      this.worker.terminate();
      this.worker = undefined;
      this._teamUuid = undefined;
      this.waitingForResponse = {};
      this.receivedClientMessage = false;
    }
  }

  /**
   * Returns whether the worker is currently initialized.
   */
  isInitialized(): boolean {
    return !!this.worker;
  }
}

export const quadraticCore = new QuadraticCore();
